import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { HiX } from 'react-icons/hi';
import LoadingSpinner from '@/components/common/ui/LoadingSpinner';
import type { EmbedShape, PostEmbed } from '@/utils/postEmbed';

export interface OpenPost {
  // null cuando solo hay embed (p. ej. desde la clave del post): no hay enlace de salida.
  url: string | null;
  embed: PostEmbed;
  title?: string;
}

interface Props {
  post: OpenPost | null;
  onClose: () => void;
}

const FRAME_SIZE: Record<EmbedShape, string> = {
  portrait: 'h-[640px] w-[min(360px,calc(100vw-2rem))]',
  landscape: 'aspect-video w-[min(800px,88vw)]',
  card: 'h-[680px] w-[min(500px,88vw)]',
};

// Instagram necesita algo mas de alto que TikTok para que el pie del embed no quede cortado.
function frameClass(embed: PostEmbed): string {
  if (embed.shape === 'portrait' && embed.network !== 'instagram') {
    return 'h-[600px] w-[min(340px,calc(100vw-2rem))]';
  }
  return FRAME_SIZE[embed.shape];
}

/**
 * Visor in-app de una publicacion: el reproductor oficial de la red dentro de un modal,
 * con salida a la red siempre visible porque el iframe es de otro origen y no podemos
 * saber si la publicacion sigue existiendo.
 */
const PostViewerModal: React.FC<Props> = ({ post, onClose }) => {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const embed = post?.embed;
  const name = post?.title || embed?.label || '';
  const loading = embed ? loadedSrc !== embed.src : false;

  // Al cerrar se desmonta el iframe; al reabrir el mismo post tiene que volver a cargar.
  useEffect(() => {
    if (!post) setLoadedSrc(null);
  }, [post]);

  return (
    <Dialog open={post !== null} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="flex max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <DialogTitle className="flex-1 truncate text-sm font-semibold text-gray-900">
              {name}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close post"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              <HiX aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
          <div className="relative min-h-0 overflow-y-auto">
            {embed && (
              <>
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white">
                    <LoadingSpinner hideMessage />
                  </div>
                )}
                <iframe
                  key={embed.src}
                  src={embed.src}
                  title={`Post: ${name}`}
                  allow="encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                  onLoad={() => setLoadedSrc(embed.src)}
                  className={clsx('block border-0', frameClass(embed))}
                />
              </>
            )}
          </div>
          {post?.url && embed && (
            <a
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block border-t border-gray-100 px-3 py-2.5 text-xs font-semibold text-primary-700 hover:text-primary-800"
            >
              Open in {embed.label} ↗
            </a>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default PostViewerModal;
